import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Link, Redirect, useHistory } from "react-router-dom";
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaClipboardCheck,
  FaClipboardList,
  FaHome,
  FaHourglassHalf,
  FaSyncAlt,
  FaTimesCircle,
  FaUserClock,
  FaUsers,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import AttendancePieChart from "../ChildComponent/AttendancePieChart ";
import BackButton from "../ChildComponent/BackButton";
import CalendarView from "../ChildComponent/CalendarView";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

//Created By Cursor
function startOfDay(date = new Date()) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

//Created By Cursor
function formatDateParts(date) {
  const d = startOfDay(date);
  const day = d.getDate();
  const month = d.getMonth() + 1;
  const year = d.getFullYear();
  const mm = month > 9 ? String(month) : `0${month}`;
  const dd = day > 9 ? String(day) : `0${day}`;
  return {
    date: d,
    iso: `${year}-${mm}-${dd}`,
    display: `${dd}-${mm}-${year}`,
  };
}

//Created By Cursor
function shiftDays(date, amount) {
  const d = startOfDay(date);
  d.setDate(d.getDate() + amount);
  return d;
}

//Created By Cursor
function todayAttendancePath(item) {
  const noSection = item.section === "No" || item.section_id === "No";
  const sectionId = noSection ? "No" : item.section_id;
  const sectionName = noSection ? "null" : item.section_name || "null";
  return `/AdminMain/TodayAttendance/${item.class_id}/${sectionId}/${encodeURIComponent(
    item.class_name || ""
  )}/${encodeURIComponent(sectionName)}`;
}

//Created By Cursor
function classLabel(item) {
  if (item.section === "No" || item.section_id === "No" || !item.section_name) {
    return item.class_name || "—";
  }
  return `${item.class_name}:${item.section_name}`;
}

//Created By Cursor
function findAttendance(statusRows, item) {
  if (!Array.isArray(statusRows) || !item) return null;
  return (
    statusRows.find(
      (row) =>
        String(row.class_id) === String(item.class_id) &&
        String(row.section_id) === String(item.section_id)
    ) || null
  );
}

//Created By Cursor
function findStudentCount(studentCounts, item) {
  if (!Array.isArray(studentCounts) || !item) return 0;
  const row = studentCounts.find(
    (entry) =>
      String(entry.class_id) === String(item.class_id) &&
      String(entry.section_id) === String(item.section_id)
  );
  return Number(row?.total_student || 0);
}

//Created By Cursor
export default function AttendanceDashboard() {
  const history = useHistory();
  const loggedIn = localStorage.getItem("token") != null;

  const [loading, setLoading] = useState(true);
  const [classBoard, setClassBoard] = useState([]);
  const [classMedium, setClassMedium] = useState([]);
  const [classList, setClassList] = useState([]);
  const [statusRows, setStatusRows] = useState([]);
  const [studentCounts, setStudentCounts] = useState([]);
  const [boardId, setBoardId] = useState("");
  const [mediumId, setMediumId] = useState("");
  const [dateMode, setDateMode] = useState("today");
  const [selectedDate, setSelectedDate] = useState(() => startOfDay(new Date()));
  const [showCalendar, setShowCalendar] = useState(false);

  const dateParts = useMemo(
    () => formatDateParts(selectedDate),
    [selectedDate]
  );

  const dateModeLabel =
    dateMode === "today"
      ? "Today"
      : dateMode === "yesterday"
        ? "Yesterday"
        : "Custom";

  const sessionBounds = useMemo(() => {
    const session = SessionManager.getCurrentSession()?.[0] || {};
    return {
      start: session.START_DATE || session.start_date || "",
      end: session.END_DATE || session.end_date || "",
    };
  }, []);

  const loadStatus = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    const session = SessionManager.getCurrentSession();
    const sessionId = session?.[0]?.session_id;
    if (!schoolCode || !sessionId) {
      setLoading(false);
      myToast.notify_warning("Missing school or session.");
      return;
    }

    setLoading(true);
    try {
      const params = {
        school_code: schoolCode,
        session_id: sessionId,
        days: "1",
        date: dateParts.iso,
      };
      const url = `${API_BASE()}getClassAttendenceStatus2.php`;
      console.log("Full URL: ", `${url}?${new URLSearchParams(params)}`);

      const response = await axios.get(url, { params }).then((res) => res.data);
      if (response?.api_status === "OK") {
        const boards = Array.isArray(response.data?.board)
          ? response.data.board
          : [];
        const mediums = Array.isArray(response.data?.medium)
          ? response.data.medium
          : [];
        const classes = Array.isArray(response.data?.class_list)
          ? response.data.class_list
          : [];
        const status = Array.isArray(response.data?.ClassAttendenceStatus)
          ? response.data.ClassAttendenceStatus
          : [];
        const counts = Array.isArray(response.data?.student_count)
          ? response.data.student_count
          : [];

        setClassBoard(boards);
        setClassMedium(mediums);
        setClassList(classes);
        setStatusRows(status);
        setStudentCounts(counts);
        setBoardId((prev) => prev || boards[0]?.board_id || "");
        setMediumId((prev) => prev || mediums[0]?.medium_id || "");
      } else {
        setClassBoard([]);
        setClassMedium([]);
        setClassList([]);
        setStatusRows([]);
        setStudentCounts([]);
        myToast.notify_warning(response?.api_remark || "Failed to load attendance.");
      }
    } catch (error) {
      console.error(error);
      myToast.notify_warning("Failed to load attendance.");
    } finally {
      setLoading(false);
    }
  }, [dateParts.iso]);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadStatus();
  }, [loadStatus]);

  const filteredClassSections = useMemo(() => {
    if (!boardId || !mediumId || !classList.length) return [];

    const filtered = classList.filter(
      (item) =>
        String(item.board_id) === String(boardId) &&
        String(item.medium_id) === String(mediumId)
    );

    const rows = [];
    filtered.forEach((item) => {
      if (item.section === "No") {
        rows.push({
          ...item,
          section_id: "No",
          section_name: "",
        });
      } else if (Array.isArray(item.section)) {
        item.section.forEach((section) => {
          rows.push({
            ...section,
            board_id: item.board_id,
            board_name: item.board_name,
            medium_id: item.medium_id,
            medium_name: item.medium_name,
            class_id: section.class_id || item.class_id,
            class_name: section.class_name || item.class_name,
          });
        });
      }
    });
    return rows;
  }, [boardId, mediumId, classList]);

  const cards = useMemo(() => {
    return filteredClassSections.map((item) => {
      const attendance = findAttendance(statusRows, item);
      const taken = attendance?.status === "Taken";
      const students = findStudentCount(studentCounts, item);
      return {
        item,
        key: `${item.class_id}-${item.section_id}`,
        label: classLabel(item),
        taken,
        students,
        present: taken ? attendance?.P ?? "0" : "N/A",
        absent: taken ? attendance?.A ?? "0" : "N/A",
        leave: taken ? attendance?.L ?? "0" : "N/A",
        btnLabel: taken ? "View Attendance" : "Take Attendance",
        to: todayAttendancePath(item),
      };
    });
  }, [filteredClassSections, statusRows, studentCounts]);

  const stats = useMemo(() => {
    const takenCards = cards.filter((c) => c.taken);
    const pendingCards = cards.filter((c) => !c.taken);
    const present = takenCards.reduce(
      (sum, c) => sum + (Number(c.present) || 0),
      0
    );
    const absent = takenCards.reduce(
      (sum, c) => sum + (Number(c.absent) || 0),
      0
    );
    const leave = takenCards.reduce(
      (sum, c) => sum + (Number(c.leave) || 0),
      0
    );
    const totalStudents = cards.reduce(
      (sum, c) => sum + (Number(c.students) || 0),
      0
    );
    const marked = present + absent + leave;
    const pendingStudents = Math.max(0, totalStudents - marked);
    const pct = (n) =>
      totalStudents === 0 ? 0 : Math.floor((n * 100) / totalStudents);
    return {
      total: cards.length,
      taken: takenCards.length,
      pending: pendingCards.length,
      totalStudents,
      present,
      absent,
      leave,
      marked,
      pendingStudents,
      presentPct: pct(present),
      absentPct: pct(absent),
      leavePct: pct(leave),
      pendingPct: pct(pendingStudents),
    };
  }, [cards]);

  const todayLabel = useMemo(() => dateParts.display, [dateParts.display]);

  const selectToday = () => {
    setDateMode("today");
    setSelectedDate(startOfDay(new Date()));
    setShowCalendar(false);
  };

  const selectYesterday = () => {
    setDateMode("yesterday");
    setSelectedDate(shiftDays(new Date(), -1));
    setShowCalendar(false);
  };

  const openCustomCalendar = () => {
    setShowCalendar(true);
  };

  const onCustomDatePick = (date) => {
    const picked = startOfDay(date);
    if (picked > startOfDay(new Date())) {
      myToast.notify_warning("Future dates are not allowed.");
      return;
    }
    setDateMode("custom");
    setSelectedDate(picked);
    setShowCalendar(false);
  };

  if (!loggedIn) return <Redirect to="/Login" />;

  return (
    <div
      className="content payroll-page sm-page atd-page"
      style={{ fontFamily: PAGE_FONT, background: "#ffffff" }}
    >
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header d-flex align-items-center gap-2">
          <button
            type="button"
            className="qb-home-btn"
            aria-label="Home"
            onClick={() => history.push("/AdminMain")}
          >
            <FaHome />
          </button>
          <ul className="breadcrumbs mb-0">
            <li>
              <span style={{ color: COLORS.white }}>Attendance Dashboard</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell atd-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaClipboardCheck />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Attendance Dashboard</h1>
              <p className="sm-hero__sub">
                {dateModeLabel} · {todayLabel} · {stats.taken} taken ·{" "}
                {stats.pending} pending
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <select
              className="pp-pill-select"
              value={boardId}
              onChange={(e) => setBoardId(e.target.value)}
              disabled={loading || classBoard.length === 0}
              aria-label="Board"
            >
              {classBoard.length === 0 ? (
                <option value="">No boards</option>
              ) : (
                classBoard.map((item) => (
                  <option key={item.board_id} value={item.board_id}>
                    {item.board_name}
                  </option>
                ))
              )}
            </select>
            <select
              className="pp-pill-select"
              value={mediumId}
              onChange={(e) => setMediumId(e.target.value)}
              disabled={loading || classMedium.length === 0}
              aria-label="Medium"
            >
              {classMedium.length === 0 ? (
                <option value="">No medium</option>
              ) : (
                classMedium.map((item) => (
                  <option key={item.medium_id} value={item.medium_id}>
                    {item.medium_name}
                  </option>
                ))
              )}
            </select>
            <button
              type="button"
              className={`pp-icon-btn${loading ? " spinning" : ""}`}
              onClick={loadStatus}
              disabled={loading}
              aria-label="Refresh"
              title="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
          </div>
        </header>

        <div className="atd-nav-actions" aria-label="Attendance views">
          <Link to="/AdminMain/MonthAttendance" className="atd-nav-btn">
            <span className="atd-nav-btn__icon" aria-hidden>
              <FaCalendarAlt />
            </span>
            <span className="atd-nav-btn__text">
              <strong>Monthly View Attendance</strong>
              <small>Class calendar · take or review by day</small>
            </span>
          </Link>
          <Link to="/AdminMain/StudentAttendance" className="atd-nav-btn">
            <span className="atd-nav-btn__icon is-list" aria-hidden>
              <FaClipboardList />
            </span>
            <span className="atd-nav-btn__text">
              <strong>Attendance Student</strong>
              <small>Student list · current month stats</small>
            </span>
          </Link>
        </div>

        <div className="atd-body">
          <section className="mat-report-card atd-report">
            <div className="mat-report-card__head atd-report-head">
              <div className="mat-report-card__title">
                <span className="mat-report-card__icon" aria-hidden>
                  <FaClipboardCheck />
                </span>
                <div className="min-w-0">
                  <h2>Attendance report</h2>
                  <div className="atd-report-subrow">
                    <p>
                      {dateModeLabel} · {todayLabel}
                      {loading
                        ? " · Loading…"
                        : stats.taken
                          ? ` · ${stats.taken} class${
                              stats.taken === 1 ? "" : "es"
                            } taken`
                          : " · No attendance taken yet"}
                    </p>
                    <div
                      className="atd-date-seg"
                      role="tablist"
                      aria-label="Report date"
                    >
                      <button
                        type="button"
                        role="tab"
                        aria-selected={dateMode === "today"}
                        className={`atd-date-seg__btn${
                          dateMode === "today" ? " is-active" : ""
                        }`}
                        onClick={selectToday}
                        disabled={loading}
                      >
                        Today
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={dateMode === "yesterday"}
                        className={`atd-date-seg__btn${
                          dateMode === "yesterday" ? " is-active" : ""
                        }`}
                        onClick={selectYesterday}
                        disabled={loading}
                      >
                        Yesterday
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={dateMode === "custom" || showCalendar}
                        className={`atd-date-seg__btn${
                          dateMode === "custom" || showCalendar
                            ? " is-active"
                            : ""
                        }`}
                        onClick={openCustomCalendar}
                      >
                        <FaCalendarAlt aria-hidden /> Custom
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <span
                className={`mat-report-badge${
                  !loading && stats.taken ? "" : " mat-report-badge--pending"
                }`}
              >
                {loading
                  ? "…"
                  : stats.taken
                    ? dateModeLabel
                    : "Pending"}
              </span>
            </div>

            {loading ? (
              <div className="atd-loading atd-loading--report">
                <MyLoader />
              </div>
            ) : (
              <>
                <div className="mat-report-body">
                  <div className="mat-report-chart">
                    <AttendancePieChart
                      Present={stats.taken ? stats.present : "N/A"}
                      Absent={stats.taken ? stats.absent : "N/A"}
                      Leave={stats.taken ? stats.leave : "N/A"}
                      size={180}
                      showLegend
                    />
                  </div>

                  <div className="mat-report-breakdown">
                    <div className="mat-report-bar is-p">
                      <div className="mat-report-bar__meta">
                        <span>
                          <FaCheckCircle aria-hidden /> Present
                        </span>
                        <strong>
                          {stats.present}
                          <small>{stats.presentPct}%</small>
                        </strong>
                      </div>
                      <div className="mat-report-bar__track" aria-hidden>
                        <i
                          style={{
                            width: `${Math.min(100, stats.presentPct)}%`,
                          }}
                        />
                      </div>
                    </div>

                    <div className="mat-report-bar is-a">
                      <div className="mat-report-bar__meta">
                        <span>
                          <FaTimesCircle aria-hidden /> Absent
                        </span>
                        <strong>
                          {stats.absent}
                          <small>{stats.absentPct}%</small>
                        </strong>
                      </div>
                      <div className="mat-report-bar__track" aria-hidden>
                        <i
                          style={{
                            width: `${Math.min(100, stats.absentPct)}%`,
                          }}
                        />
                      </div>
                    </div>

                    <div className="mat-report-bar is-l">
                      <div className="mat-report-bar__meta">
                        <span>
                          <FaCalendarAlt aria-hidden /> Leave
                        </span>
                        <strong>
                          {stats.leave}
                          <small>{stats.leavePct}%</small>
                        </strong>
                      </div>
                      <div className="mat-report-bar__track" aria-hidden>
                        <i
                          style={{
                            width: `${Math.min(100, stats.leavePct)}%`,
                          }}
                        />
                      </div>
                    </div>

                    <div className="mat-report-bar is-pending">
                      <div className="mat-report-bar__meta">
                        <span>
                          <FaHourglassHalf aria-hidden /> Pending
                        </span>
                        <strong>
                          {stats.pendingStudents}
                          <small>{stats.pendingPct}%</small>
                        </strong>
                      </div>
                      <div className="mat-report-bar__track" aria-hidden>
                        <i
                          style={{
                            width: `${Math.min(100, stats.pendingPct)}%`,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mat-report-foot">
                  <div className="mat-report-chip">
                    <span className="mat-report-chip__icon is-sky" aria-hidden>
                      <FaUsers />
                    </span>
                    <div>
                      <span>Total students</span>
                      <strong>{stats.totalStudents}</strong>
                    </div>
                  </div>
                  <div className="mat-report-chip">
                    <span className="mat-report-chip__icon is-mint" aria-hidden>
                      <FaCheckCircle />
                    </span>
                    <div>
                      <span>Taken classes</span>
                      <strong>{stats.taken}</strong>
                    </div>
                  </div>
                  <div
                    className={`mat-report-chip${
                      stats.pending > 0 ? " is-warn" : ""
                    }`}
                  >
                    <span
                      className={`mat-report-chip__icon${
                        stats.pending > 0 ? " is-warn" : " is-sky"
                      }`}
                      aria-hidden
                    >
                      {stats.pending > 0 ? (
                        <FaHourglassHalf />
                      ) : (
                        <FaUserClock />
                      )}
                    </span>
                    <div>
                      <span>Pending classes</span>
                      <strong>{stats.pending}</strong>
                    </div>
                  </div>
                </div>
              </>
            )}
          </section>

          {!loading && (
            <>
              <section className="atd-section">
                <div className="atd-section__head">
                  <h2 className="atd-section__title">Quick status</h2>
                </div>
                {cards.length === 0 ? (
                  <div className="atd-empty">
                    No classes for the selected board and medium.
                  </div>
                ) : (
                  <div className="atd-class-list" role="list">
                    {cards.map((card) => (
                      <Link
                        key={`list-${card.key}`}
                        to={card.to}
                        role="listitem"
                        className={`atd-class-row ${
                          card.taken
                            ? "atd-class-row--taken"
                            : "atd-class-row--pending"
                        }`}
                        title={card.btnLabel}
                      >
                        {card.taken ? (
                          <span className="atd-class-row__status" aria-hidden />
                        ) : (
                          <span
                            className="atd-class-row__status atd-class-row__status--empty"
                            aria-hidden
                          />
                        )}
                        <span className="atd-class-row__name">{card.label}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </section>

              <section className="atd-section atd-section--last">
                <div className="atd-section__head">
                  <h2 className="atd-section__title">
                    {dateMode === "custom"
                      ? "Custom overview"
                      : `${dateModeLabel}’s overview`}
                  </h2>
                  <p className="atd-section__hint">
                    Present, absent and leave for each class / section ·{" "}
                    {todayLabel}
                  </p>
                </div>
                {cards.length === 0 ? (
                  <div className="atd-empty">
                    No attendance cards to show.
                  </div>
                ) : (
                  <div className="atd-cards">
                    {cards.map((card) => (
                      <article
                        key={`card-${card.key}`}
                        className={`atd-card ${
                          card.taken ? "atd-card--taken" : "atd-card--pending"
                        }`}
                      >
                        <div className="atd-card__head">
                          <div className="min-w-0">
                            <h3 className="atd-card__title">{card.label}</h3>
                            <p className="atd-card__sub">
                              {card.taken
                                ? `Attendance recorded · ${todayLabel}`
                                : `Not taken · ${todayLabel}`}
                            </p>
                          </div>
                          <span
                            className={`atd-card__badge ${
                              card.taken
                                ? "atd-card__badge--taken"
                                : "atd-card__badge--pending"
                            }`}
                          >
                            {card.taken ? "Taken" : "Pending"}
                          </span>
                        </div>
                        <div className="atd-card__chart">
                          <AttendancePieChart
                            Present={card.present}
                            Absent={card.absent}
                            Leave={card.leave}
                            size={108}
                            showLegend
                          />
                        </div>
                        <Link
                          to={card.to}
                          className={`atd-card__cta${
                            card.taken
                              ? " atd-card__cta--view"
                              : " atd-card__cta--take"
                          }`}
                        >
                          {card.btnLabel}
                        </Link>
                      </article>
                    ))}
                  </div>
                )}
              </section>
            </>
          )}

          {showCalendar
            ? createPortal(
                <div className="atd-cal-modal" role="dialog" aria-modal="true">
                  <button
                    type="button"
                    className="atd-cal-modal__backdrop"
                    aria-label="Close calendar"
                    onClick={() => setShowCalendar(false)}
                  />
                  <div
                    className="atd-cal-modal__panel"
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <div className="atd-cal-modal__hero">
                      <div className="atd-cal-modal__hero-icon" aria-hidden>
                        <FaCalendarAlt />
                      </div>
                      <div className="min-w-0">
                        <h3>Select date</h3>
                        <p>Pick any past date to load attendance</p>
                      </div>
                      <button
                        type="button"
                        className="atd-cal-modal__close"
                        onClick={() => setShowCalendar(false)}
                      >
                        ✕
                      </button>
                    </div>

                    <div className="atd-cal-modal__body">
                      <label className="atd-cal-native">
                        <span>Quick pick</span>
                        <input
                          type="date"
                          max={formatDateParts(new Date()).iso}
                          value={dateParts.iso}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (!value) return;
                            onCustomDatePick(new Date(`${value}T00:00:00`));
                          }}
                        />
                      </label>

                      <div className="atd-cal-modal__calendar">
                        <CalendarView
                          events={[]}
                          columns={7}
                          subtitle=""
                          disableFutureDates
                          selectedDate={selectedDate}
                          showLegend={false}
                          onDayPress={onCustomDatePick}
                          onDayLongPress={() => {}}
                          onPrevPress={() => {}}
                          onNextPress={() => {}}
                          start_date={sessionBounds.start || "2000-01-01"}
                          end_date={
                            sessionBounds.end ||
                            formatDateParts(new Date()).iso
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>,
                document.body
              )
            : null}
        </div>
      </div>
    </div>
  );
}
